import { Firmware, FirmwareQueryParam, FirmwareUploadParam } from '/@/types/device-firmware'
import request, { CommonListResponse, IListWorkspaceResponse, IPage, IWorkspaceResponse } from '/@/api/http/request'
import { Device } from '/@/types/device'
import { ELocalStorageKey } from '../types'
import { message } from 'ant-design-vue'
import apiPilot from '/@/api/pilot-bridge'
import { CURRENT_CONFIG } from './http/config'
import axios from 'axios'

const HTTP_PREFIX = '/manage/api/v1'
const delay = (ms:number) => new Promise(resolve => setTimeout(resolve, ms))

// const DRONOS_ACCOUNT_URI_PREFIX = 'http://localhost:3001'
// const DRONOS_MISSION_URI_PREFIX = 'http://localhost:3008'
// const DRONOS_STREAM_RESOURCE_URI_PREFIX = 'http://localhost:3019'
// const DRONOS_URI_PREFIX = 'http://localhost'

const DRONOS_URI_PREFIX = CURRENT_CONFIG.dronosBaseURL

// login
export interface LoginBody {
  email: string,
  password: string,
  flag: number,
}
export interface BindBody {
  device_sn: string,
  user_id: string,
  workspace_id: string,
  domain?: string
}
export interface HmsQueryBody {
  sns: string[],
  children_sn: string,
  device_sn: string,
  language: string,
  level: number | string,
  begin_time: number,
  end_time: number,
  message: string,
  domain: number,
}

export const login = async function (body: LoginBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/login`
  const result = await request.post(url, body, { withCredentials: true })
  return result.data
}

// Login
export const dronosLogin = async function (body: LoginBody) {
  const url = `${DRONOS_URI_PREFIX}/account/auth/login`
  const result = await axios.post(url, body, { withCredentials: true })
  return result
}

// Refresh Token
export const refreshToken = async function (body: {}) {
  const url = `${DRONOS_URI_PREFIX}/account/auth/refresh-token`
  const result = await axios.post(url, body, { withCredentials: true })
  return result.data
}

// User Profile
export const userProfile = async function () {
  const token = localStorage.getItem(ELocalStorageKey.Token)
  const url = `${DRONOS_URI_PREFIX}/account/users/profile`
  const result = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    }
  })
  localStorage.setItem(ELocalStorageKey.UserData, JSON.stringify(result.data))

  const user = result.data
  const activeWorkspaceId = user.activeWorkspaceId
  const matchingMember = user.member.find(
    (m: any) => m.workspace && m.workspace._id === activeWorkspaceId
  )

  localStorage.setItem(ELocalStorageKey.WorkspaceName, matchingMember?.workspace?.name)
  localStorage.setItem(ELocalStorageKey.WorkspaceDesc, matchingMember?.workspace?.name)
  localStorage.setItem(ELocalStorageKey.PlatformName, 'DRONOS')
  return result
}

// Current User's Active Mission Tracking Id
export const activeMissionTrackingId = function (): string | null {
  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId
  const matchingMember = user.member.find(
    (m: any) => m.workspace && m.workspace._id === activeWorkspaceId
  )

  const activeMissionTrackingId = matchingMember?.settings?.activeMissionTrackingId ?? null

  console.log('Active Mission ID:', activeMissionTrackingId)
  return activeMissionTrackingId
}

// Validate Mission Tracking Id
export const validateMissionTrackingId = async function (missionTrackingId: string) {
  const token = localStorage.getItem(ELocalStorageKey.Token)

  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId

  const url = `${DRONOS_URI_PREFIX}/mission/missions`
  const result = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: activeWorkspaceId
    },
    params: { trackingId: missionTrackingId }
  })

  return result.data
}

// Get Mission Objectives
export const getMissionObjectives = async function () {
  const token = localStorage.getItem(ELocalStorageKey.Token)

  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId

  const url = `${DRONOS_URI_PREFIX}/mission/mission-objective`
  const result = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: activeWorkspaceId
    },
  })

  return result.data
}

// Get Mission Objectives By Ids
export const getMissionObjectivesByIds = async function () {
  const token = localStorage.getItem(ELocalStorageKey.Token)

  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId

  const url = `${DRONOS_URI_PREFIX}/mission/mission-objective/list-by-ids`
  const result = await axios.post(url, { missionObjectiveIds: CURRENT_CONFIG.missionObjectiveIds }, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: activeWorkspaceId
    },
  })

  return result.data
}

// Create New Mission
export const createMission = async function () {
  const token = localStorage.getItem(ELocalStorageKey.Token)

  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId
  const url = `${DRONOS_URI_PREFIX}/mission/missions`

  const currentDate = new Date()
  const formattedDate = currentDate.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })

  const createMissionDTO = {
    name: `DJI Flights ${formattedDate}`,
    objective: (await getMissionObjectivesByIds()).records[0]._id,
    status: 'DRAFT',
    workspaceId: activeWorkspaceId
  }

  const result = await axios.post(url, { ...createMissionDTO }, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: activeWorkspaceId
    }
  })

  return result.data
}

// Update Active Mission Tracking Id in Member Settings
export const updateMemberSettings = async function (missionTrackingId: string) {
  const userRaw = localStorage.getItem(ELocalStorageKey.UserData)
  if (!userRaw) {
    console.warn('No user data found')
    return null
  }

  const user = JSON.parse(userRaw)
  const activeWorkspaceId = user.activeWorkspaceId
  const matchingMember = user.member.find(
    (m: any) => m.workspace && m.workspace._id === activeWorkspaceId
  )

  const token = localStorage.getItem(ELocalStorageKey.Token)
  const url = `${DRONOS_URI_PREFIX}/account/members/${matchingMember._id}`

  const result = await axios.patch(url, {
    settings: {
      activeMissionTrackingId: missionTrackingId
    }
  }, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: matchingMember.workspace._id
    },
  })

  return result
}

// Get Active RTMP Url
export const getActiveRtmpUrl = async function () {
  const missionTrackingId = activeMissionTrackingId()!
  const missionData = await validateMissionTrackingId(missionTrackingId)

  if (missionData?.records?.length <= 0) {
    console.error('Mission not found.')
    message.error('Mission not found.')
    return false
  }

  const token = localStorage.getItem(ELocalStorageKey.Token)
  const url = `${DRONOS_URI_PREFIX}/stream-resource/medialive-streams/get-active-rtmp-url`

  const result = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: missionData?.records[0]?.workspaceId,
    },
    params: {
      workspaceId: missionData?.records[0]?.workspaceId,
      missionId: missionData?.records[0]?._id
    }
  })

  return result.data
}

// Create Live Stream
export const createLiveStream = async function (requireAiAnnotation: boolean, djiDroneSN: string) {
  console.log('Creating live stream with DJI Drone SN:', djiDroneSN)
  if (!djiDroneSN || !djiDroneSN.trim()) {
    message.error('Aircraft SN is missing. Please reconnect the drone and retry.')
    throw new Error('Missing aircraft SN in createLiveStream')
  }

  const missionTrackingId = activeMissionTrackingId()!
  const missionData = await validateMissionTrackingId(missionTrackingId)

  if (missionData?.records?.length <= 0) {
    console.error('Mission not found.')
    message.error('Mission not found.')
    return false
  }

  const createLiveastreamDTO = {
    workspaceId: missionData?.records[0]?.workspaceId,
    missionTrackingId: missionTrackingId,
    missionId: missionData?.records[0]?._id,
    requireAiAnnotation,
    isAiStreamAvailable: false,
    isDroneStreamAvailable: false,
    djiDroneSN, // '9N9CN3P00139DD'
  }

  console.log('[createLiveStream] request payload:', createLiveastreamDTO)

  const token = localStorage.getItem(ELocalStorageKey.Token)
  const url = `${DRONOS_URI_PREFIX}/stream-resource/live-streams`

  const result = await axios.post(url, { ...createLiveastreamDTO }, {
    headers: {
      Authorization: `Bearer ${token}`,
      workspaceId: missionData?.records[0]?.workspaceId,
    }
  })

  await delay(30000)
  return result.data
}

// Get Platform Info
export const getPlatformInfo = async function (): Promise<IWorkspaceResponse<any>> {
  // const url = `${HTTP_PREFIX}/workspaces/current`
  // const result = await request.get(url)
  // return result.data
  return {
    code: 0,
    message: 'success',
    data: {
      id: 2,
      workspace_id: localStorage.getItem(ELocalStorageKey.WorkspaceId),
      workspace_name: localStorage.getItem(ELocalStorageKey.WorkspaceName),
      workspace_desc: localStorage.getItem(ELocalStorageKey.WorkspaceDesc),
      platform_name: localStorage.getItem(ELocalStorageKey.PlatformName),
      bind_code: 'dhp' // Workspace Example - Dubai Holding Property
    }
  }
}

// Get User Info
export const getUserInfo = async function (): Promise<IWorkspaceResponse<any>> {
  // const url = `${HTTP_PREFIX}/users/current`
  // const result = await request.get(url)
  // return result.data

  return {
    code: 0,
    message: 'success',
    data: {
      username: localStorage.getItem(ELocalStorageKey.Username),
      user_id: localStorage.getItem(ELocalStorageKey.UserId),
      workspace_id: localStorage.getItem(ELocalStorageKey.WorkspaceId),
      user_type: 2,
      mqtt_username: CURRENT_CONFIG.mqttUsername,
      mqtt_password: CURRENT_CONFIG.mqttPassword,
      access_token: CURRENT_CONFIG.mqttAccessToken,
      mqtt_addr: CURRENT_CONFIG.mqttHost
    }
  }
}

// Get Device Topo
export const getDeviceTopo = async function (workspace_id: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices`
  const result = await request.get(url)
  return result.data
}

// Get Livestream Capacity
export const getLiveCapacity = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/capacity`
  const result = await request.get(url, body)
  return result.data
}

// Start Livestream
export const startLivestream = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/start`
  const result = await request.post(url, body)
  return result.data
}

// Stop Livestream
export const stopLivestream = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/stop`
  const result = await request.post(url, body)
  return result.data
}
// Update Quality
export const setLivestreamQuality = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/update`
  const result = await request.post(url, body)
  return result.data
}

export const getAllUsersInfo = async function (wid: string, body: IPage): Promise<CommonListResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users?&page=${body.page}&page_size=${body.page_size}`
  const result = await request.get(url)
  return result.data
}

export const updateUserInfo = async function (wid: string, user_id: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/users/${wid}/users/${user_id}`
  const result = await request.put(url, body)
  return result.data
}

export const bindDevice = async function (body: BindBody): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${body.device_sn}/binding`
  const result = await request.post(url, body)
  return result.data
}

export const unbindDevice = async function (device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${device_sn}/unbinding`
  const result = await request.delete(url)
  return result.data
}

export const getDeviceBySn = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.get(url)
  return result.data
}

/**
 * 获取绑定设备信息
 * @param workspace_id
 * @param body
 * @param domain
 * @returns
 */
export const getBindingDevices = async function (workspace_id: string, body: IPage, domain: number): Promise<IListWorkspaceResponse<Device>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/bound?&page=${body.page}&page_size=${body.page_size}&domain=${domain}`
  const result = await request.get(url)
  return result.data
}

export const updateDevice = async function (body: {}, workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/${device_sn}`
  const result = await request.put(url, body)
  return result.data
}

export const getUnreadDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.get(url)
  return result.data
}

export const updateDeviceHms = async function (workspace_id: string, device_sn: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms/${device_sn}`
  const result = await request.put(url)
  return result.data
}

export const getDeviceHms = async function (body: HmsQueryBody, workspace_id: string, pagination: IPage): Promise<IListWorkspaceResponse<any>> {
  let url = `${HTTP_PREFIX}/devices/${workspace_id}/devices/hms?page=${pagination.page}&page_size=${pagination.page_size}` +
    `&level=${body.level ?? ''}&begin_time=${body.begin_time ?? ''}&end_time=${body.end_time ?? ''}&message=${body.message ?? ''}&language=${body.language}`
  body.sns.forEach((sn: string) => {
    if (sn !== '') {
      url = url.concat(`&device_sn=${sn}`)
    }
  })
  const result = await request.get(url)
  return result.data
}

export const changeLivestreamLens = async function (body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/live/streams/switch`
  const result = await request.post(url, body)
  return result.data
}

export const getFirmwares = async function (workspace_id: string, page: IPage, body: FirmwareQueryParam): Promise<IListWorkspaceResponse<Firmware>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspace_id}/firmwares?page=${page.page}&page_size=${page.page_size}` +
    `&device_name=${body.device_name}&product_version=${body.product_version}&status=${body.firmware_status ?? ''}`
  const result = await request.get(url)
  return result.data
}

export const importFirmareFile = async function (workspaceId: string, param: FormData): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/file/upload`
  const result = await request.post(url, param)
  return result.data
}

export const changeFirmareStatus = async function (workspaceId: string, firmwareId: string, param: {status: boolean}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/firmwares/${firmwareId}`
  const result = await request.put(url, param)
  return result.data
}
